/**
 * @file DispatchTopBar.tsx
 * @description Header console bar for TableGreet Dispatcher delivery terminal with TableGreet brand identity.
 */

import React from 'react';
import { Wifi, LogOut } from 'lucide-react';
import logo from '../../assets/logo.svg';

interface DispatchTopBarProps {
  venueName: string;
  filterStatus: string;
  onFilterChange: (st: string) => void;
  orderCount: number;
  onLogout: () => void;
}

const FILTERS = [
  { id: 'all', label: 'All Deliveries' },
  { id: 'pending', label: 'Pending' },
  { id: 'preparing', label: 'Preparing' },
  { id: 'ready', label: 'Ready for Driver' },
  { id: 'out_for_delivery', label: 'Out for Delivery' },
  { id: 'delivered', label: 'Delivered' },
];

export const DispatchTopBar: React.FC<DispatchTopBarProps> = ({
  venueName,
  filterStatus,
  onFilterChange,
  orderCount,
  onLogout,
}) => {
  return (
    <header className="bg-[#121212] border-b border-white/10 px-4 py-3 shrink-0">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Real TableGreet Logo & Venue Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} className="w-9 h-9 object-contain shrink-0" alt="TableGreet™" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-black text-base tracking-tight bg-gradient-to-r from-[#ff4f00] to-orange-400 bg-clip-text text-transparent">
                  TableGreet<sup className="text-[0.55em] font-bold align-super">™</sup>
                </span>
                <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-[#ff4f00]/15 text-[#ff4f00] border border-[#ff4f00]/30 tracking-wider">
                  Dispatch
                </span>
                <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-800/50">
                  <Wifi className="w-2.5 h-2.5 animate-pulse" />
                  Live
                </span>
              </div>
              <p className="text-xs text-zinc-400 font-mono mt-0.5">
                <span className="text-zinc-200 font-semibold uppercase">{venueName || 'Fleet Control'}</span>
                <span> • </span>
                <span className="text-[#ff4f00] font-bold">{orderCount} DELIVERIES</span>
              </p>
            </div>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          {FILTERS.map(f => (
            <button
              key={f.id}
              type="button"
              onClick={() => onFilterChange(f.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                filterStatus === f.id
                  ? 'bg-[#ff4f00] text-white font-black shadow-lg shadow-[#ff4f00]/25'
                  : 'bg-[#181818] text-zinc-400 border border-white/10 hover:text-white hover:bg-white/5'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            onClick={onLogout}
            className="p-2 rounded-xl bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 transition-colors cursor-pointer"
            title="Log Out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
};
